import React from "react"
import { each, groupBy } from "lodash"
import { deserializeRegExp } from "./helpers"
import { FormHelpers } from "./form_helpers"
import { IClientConfig } from "../clients/config";
import * as data from "../data"
import { IStateChangeMsg, FormStatus, IAppUrls } from "../wizard"
import Validation from "./validation"

/**
 * Includes props for all children.
 */
export interface ITopLevelProps {
  context: ISharedContext
  qualificationStepProps: IQualificationStepProps
  parentCompanyNameOptions?: string[]
  submitterParentNameOptions?: string[]
  selectablePlans?: data.IPlanParentOrganization[]
  welcomeStepProps: WelcomeStepStateProps
}

export interface WelcomeStepStateProps {
  continue_token_needed: boolean
  sent_continue_url?: boolean
  changed_submitter_id?: {
    old_submitter_id: data.SubmitterIdParam | null
    new_submitter_id: data.SubmitterIdParam
  }
  sendContinueUrl: () => void
}

export interface IQualificationStepProps {
  showQualificationResult: boolean
}

export const ViewModes = {
  NORMAL: "normal",
  DOWNLOADABLE: "downloadable",
}
export type ViewMode = typeof ViewModes[keyof typeof ViewModes]

/**
 * Context shared by the entire application.
 */
export interface ISharedContext extends ICoreProps {
  loaded: boolean
  status: FormStatus
  editable: boolean
  continue_token_needed: boolean
  csrf_expired: boolean
  current_step: number
  max_step: number
  short_id: string | null
  canEditText: boolean
  canEditSubmitterCode: boolean
  urls: IAppUrls
  active_submitters: data.IActiveSubmitter[]
  view_mode: ViewMode
}

/**
 * Minimal props needed for RegistrationFormComponent instance methods
 */
export interface ICoreProps {
  config: IClientConfig
  client: data.IClient
  form: data.IRegistrationForm | null
  attachment: data.IAttachment | null

  /**
   * Helper for creating a FORM_CHANGED msg.
   */
  setFormValue: SetFormValue

  /**
   * Generic onChange event handler that can be used for most inputs.
   */
  onInputChange: OnInputChange

  /**
   * Dispatch a message to the application that may result in a state change.
   */
  dispatch: Dispatch
}

export type SetFormValue = (names: string[], value: unknown) => void
export type OnInputChange = (event: React.FormEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
export type Dispatch = (msg: IStateChangeMsg) => void

export interface IActivatable {
  active: boolean
}

const ERROR_CLASS = "has-error"

// dom helper
function insertAfter(newNode: Element, referenceNode: Element) {
  if (!referenceNode.parentNode) {
    throw new Error("referenceNode has no parent")
  }
  referenceNode.parentNode.insertBefore(newNode, referenceNode.nextSibling)
}

export function validateForm(): boolean {
  // console.debug("validateForm")
  clearErrors()

  let isValid = true
  const form = formElement()

  const oneRequiredPlanGroups = groupBy(
    form.querySelectorAll<HTMLSelectElement>("[data-fv-onerequiredplaningroup]"),
    (el: HTMLSelectElement) => el.getAttribute("data-fv-onerequiredplaningroup")
  )
  // console.debug("oneRequiredPlanGroups", oneRequiredPlanGroups)

  each(form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>("input,select,textarea"), (el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) => {
    if (!el.validity) {
      throw new Error("no validity!")
    }

    let customError: string | null | undefined

    const validity = el.validity
    let fieldIsValid = validity.valid

    // only run these special validations if the field isn't empty
    const value = el.value
    if (value && value.length && value.length > 0) {
    // zip code validation
      const zipCodeValidationMessage = el.getAttribute("data-fv-zipcode-message")
      // don't run the zip code validation if the field is empty
      if (zipCodeValidationMessage) {
        if (!Validation.isZipCode(value)) {
          fieldIsValid = false
          customError = zipCodeValidationMessage
        }
      }

      // ein code validation
      const einValidationMessage = el.getAttribute("data-fv-ein-message")
      if (einValidationMessage) {
        if (!Validation.isEIN(value)) {
          fieldIsValid = false
          customError = einValidationMessage
        }
      }

      // naic validation
      const naicValidationMessage = el.getAttribute("data-fv-naic-message")
      if (naicValidationMessage) {
        if (!Validation.isNAIC(value)) {
          fieldIsValid = false
          customError = naicValidationMessage
        }
      }

      // does it match some other field?
      const matchField = el.getAttribute("data-fv-matches")
      const matchMessage = el.getAttribute("data-fv-matches-message")
      // don't run the zip code validation if the field is empty
      if (matchField && matchMessage) {
        const inputElt = document.querySelector<HTMLInputElement>(`input[name="${matchField}"]`)
        const matchValue = inputElt !== null ? inputElt.value : ""

        if (value.length && value.length > 0 && value !== matchValue) {
          fieldIsValid = false
          customError = matchMessage
        }
      }

      // excluded values
      const excludedValues = el.getAttribute("data-fv-excluded-values")
      if ( excludedValues ) {
        const excludedValuesArray = excludedValues.split(",")
        for ( const exclusion of excludedValuesArray ) {
          if ( value === exclusion ) {
            fieldIsValid = false
            customError = el.getAttribute("data-fv-excluded-values-message")
            break
          }
        }
      }

      // phone number regex
      const phoneError = el.getAttribute("data-fv-phone-message")
      if (phoneError && !Validation.isPhoneNumber(value)) {
        fieldIsValid = false
        customError = phoneError
      }

      // Generic regex
      const serializedRegex: unknown = el.getAttribute("data-fv-regex-match")
      if (typeof serializedRegex === "string" && serializedRegex) {
        const regexMatch = deserializeRegExp(serializedRegex)

        if (!regexMatch.test(value)) {
          fieldIsValid = false
          customError = el.getAttribute("data-fv-regex-match-error") || "Value is invalid"
        }
      }

      // date validation
      const dateValidationMessage = el.getAttribute("data-fv-is-valid-date-message")
      if (dateValidationMessage) {
        const date = new Date(value)
        if (isNaN(date.getTime()) || date < new Date(1900, 1, 1) || date > new Date(9999, 12, 31)) {
          fieldIsValid = false
          customError = dateValidationMessage
        }
      }

      // If all other validations passed, check the length
      if (fieldIsValid && typeof (el as HTMLInputElement).maxLength === "number") {
        const maxLength = (el as HTMLInputElement).maxLength
        if (maxLength > 0 && value && value.length > maxLength) {
          fieldIsValid = false
          customError = "Value is too long"
        }
      }

      // At least one of these plans must be selected
      if (el.getAttribute("data-fv-onerequiredplaningroup")) {
        const allSelects = oneRequiredPlanGroups[el.getAttribute("data-fv-onerequiredplaningroup") as string] ?? []

        let oneSelected = false
        for (const select of allSelects) {
          if (select.value && select.value !== data.NOT_APPLICABLE) {
            oneSelected = true
            break
          }
        }

        if (!oneSelected) {
          fieldIsValid = false
          customError = el.getAttribute("data-fv-onerequiredplaningroup-message") || "At least one plan must be selected"
        }
      }
    }

    if (!fieldIsValid) {
      isValid = false
    }

    const nameAttribute = el.getAttribute("name") ?? el.id
    if (!nameAttribute) {
      console.warn("not creating error message for element without a 'name' or 'id' attribute:", el)
      return
    }

    let error = document.querySelectorAll<HTMLElement>(`[data-errors-for="${nameAttribute}"]`)[0]
    if (!error) {
      error = document.createElement("span")
      error.classList.add("help-block")
      error.setAttribute("data-errors-for", nameAttribute)
      insertAfter(error, el)
    }

    let formGroup: Element | null
    const formGroupSelector = el.getAttribute("data-fv-form-group-selector")
    if (formGroupSelector) {
      formGroup = el.closest(formGroupSelector)
    } else {
      formGroup = el.parentElement
    }

    if (fieldIsValid) {
      error.innerHTML = ""
      error.style.display = "none"
    } else {
      error.style.display = "block"

      let errorMessage: string

      // https://developer.mozilla.org/en-US/docs/Web/API/ValidityState
      // valueMissing: true,
      // typeMismatch: false,
      // patternMismatch: false,
      // tooLong: false,
      // tooShort: false…
      //
      // TODO:
      // data-fv-zipcode-message
      // data-fv-ein-message
      if (customError) {
        errorMessage = customError
      } else if (validity.valueMissing) {
        errorMessage =
        el.getAttribute("data-fv-notempty-message") ||
        `Please ${el.tagName === "SELECT" ? "select" : "enter"} a value`
      } else if (validity.typeMismatch) {
      // email
        errorMessage = el.getAttribute("data-fv-emailaddress-message") || "is not in the correct format"
      } else {
        errorMessage = "is invalid"
      }

      if (el.getAttribute("data-fv-ignore-error-message") === "true") {
        errorMessage = ""
        error.style.display = "none"
      } else {
        error.style.display = "block"
      }

      error.innerHTML = errorMessage

      if (formGroup) {
        formGroup.classList.add(ERROR_CLASS)
      }
    }
  })

  if (!isValid) {
    // console.warn("form is not valid")
    let top: number | undefined

    const errors = document.getElementsByClassName(ERROR_CLASS)
    const firstError = errors.length ? errors[0] : null
    if (firstError) {
      const viewportOffset = firstError.getBoundingClientRect()
      const newTop = viewportOffset.top + window.scrollY

      if (typeof top === "undefined" || newTop < top) {
        top = newTop
      }

      if (typeof top === "undefined") {
        top = 0
      }

      window.scrollTo(0, top)
    }
  }

  return isValid
}

export function clearErrors() {
  const form = formElement()
  each(form.querySelectorAll<HTMLElement>(`.${ERROR_CLASS}`), (el: HTMLElement) => {
    el.classList.remove(ERROR_CLASS)
  })

  // Important not to remove DOM node, as some inputs depend on
  // being able to provide their own error help-block in order to
  // look good.
  each(form.querySelectorAll<HTMLElement>(".help-block[data-errors-for]"), (el: HTMLElement) => {
    el.innerHTML = ""
  })
}

/**
 * Attempt to clear the errors for a single input
 *
 * @param {object} input the input element
 */
export function clearErrorsForInput(input: HTMLInputElement) {
  const wrapper = input.closest(`.${ERROR_CLASS}`)
  if (!wrapper) {
    return
  }

  wrapper.classList.remove(ERROR_CLASS)
  const errors = wrapper.querySelectorAll(".help-block[data-errors-for]")
  if (errors && errors.length > 0) {
    errors.forEach((el) => el.innerHTML = "")
  }
}

export function formElement(): HTMLFormElement {
  const form = document.getElementById("registration-form")
  if (!form) {
    throw new Error("#registration-form not found!")
  }
  return form as HTMLFormElement
}

export function requiresQualification(client?: data.IClient | null, form?: data.IRegistrationForm | null): boolean {
  if (!client) {
    throw new Error("no client, no idea if requires qualification")
  }

  if (!form?.insurer?.type) {
    return client.requires_qualification
  }

  return data.HasQualificationSections.has(form.insurer.type) && client.requires_qualification
}

export function qualificationAnswered(form?: data.IRegistrationForm | null): boolean {
  const value = form?.eligibility?.reporting as unknown
  return typeof value === "string" && value !== ""
}

export function qualificationMet(form?: data.IRegistrationForm | null): boolean {
  return FormHelpers.str2bool(form?.eligibility?.reporting) === true
}

export function defaultContactCompany(form?: data.IRegistrationForm | null): string | undefined {
  // console.log(this.props.form)
  const uuid = form?.filler?.contact

  if (uuid) {
    const data = form?.contacts?.[uuid]
    if (data && typeof data === "object") {
      return data.company_name
    }
  }

  return undefined
}
